import { appStorage } from '../lib/safeStorage';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import OverlayPortal from './OverlayPortal';
import { pageTitles } from './layout/pageTitles';
import { useRolePreview } from '../context/RolePreviewContext';
import { staffAPI } from '../services/staffApi';

/**
 * STAFFED PRO — completion certificate.
 *
 * Tracks how many of the guided "Show me" walkthroughs a user has completed
 * (each finished tour writes `staffed_spotlight_page:<route>` = "1" to
 * appStorage). When every page walkthrough is done, it fires a one-time
 * confetti + certificate moment. A subtle progress chip lets users reopen it
 * on demand. 100% client-side — no backend needed.
 *
 * Mount once, globally (e.g. inside DashboardLayout), so it's present on every page.
 */

const AWARD_KEY = 'staffed_pro_awarded';
const GOLD = '#d4af37';

// Derive the canonical tour set straight from pageTitles so it stays in sync
// automatically as tours are added/removed. Only tours whose `audience` the
// current viewer can actually reach are counted:
//   - staff viewer  → audience in {staff, both}
//   - admin viewer  → audience in {admin, both}
// Missing audience defaults to 'admin' (safer to hide from staff).
function isAdminLikeUser(u) {
  if (!u) return false;
  if (u.role === 'staff' || u.industryType === 'staff' || u.isStaff) return false;
  if (u.role === 'vendor' || u.industryType === 'vendor' || u.isVendor) return false;
  return true;
}
function getTourEntries(viewerContext) {
  try {
    return Object.entries(pageTitles)
      .filter(([, v]) => v && Array.isArray(v.tour) && v.tour.length > 0)
      .filter(([, v]) => {
        const audience = v.audience || 'admin';
        if (audience === 'both') return true;
        return audience === viewerContext;
      })
      .map(([route, v]) => ({ route, title: v.title || route, key: `staffed_spotlight_page:${route}` }));
  } catch (e) {
    return [];
  }
}

// BLOCK S7 2 — the slim walkthrough progress line moved into the Help panel
// reads from HERE, so the count and the certificate stay one source of truth.
export function readWalkthroughProgress(viewerContext) {
  const entries = getTourEntries(viewerContext);
  const total = entries.length;
  let completed = 0;
  entries.forEach((e) => {
    try { if (appStorage.getItem(e.key) === '1') completed += 1; } catch (_e) { /* no-op */ }
  });
  return { completed, total, isComplete: total > 0 && completed >= total };
}

function readUser() {
  try {
    const u = JSON.parse(appStorage.getItem('user') || '{}');
    return {
      name: [u.firstName, u.lastName].filter(Boolean).join(' ').trim(),
      company: (u.companyName || '').trim(),
    };
  } catch (e) {
    return { name: '', company: '' };
  }
}

// Lightweight, dependency-free confetti burst.
function fireConfetti() {
  try {
    const canvas = document.createElement('canvas');
    canvas.setAttribute('aria-hidden', 'true');
    canvas.style.cssText =
      'position:fixed;inset:0;width:100vw;height:100vh;pointer-events:none;z-index:2147483002';
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    document.body.appendChild(canvas);
    const ctx = canvas.getContext('2d');
    const cols = ['#d4af37', '#f7ecb4', '#ffffff', '#c9a227', '#b8860b'];
    const parts = Array.from({ length: 170 }, () => {
      const a = Math.random() * Math.PI * 2;
      const s = 4 + 9 * Math.random();
      return {
        x: window.innerWidth / 2,
        y: window.innerHeight * 0.28,
        vx: Math.cos(a) * s,
        vy: Math.sin(a) * s - 6,
        g: 0.18 + 0.14 * Math.random(),
        size: 4 + 6 * Math.random(),
        rot: Math.random() * 6,
        vr: (Math.random() - 0.5) * 0.3,
        c: cols[(Math.random() * cols.length) | 0],
      };
    });
    const t0 = performance.now();
    const frame = (t) => {
      const e = t - t0;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      parts.forEach((p) => {
        p.vy += p.g;
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vr;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.globalAlpha = Math.max(0, 1 - e / 3200);
        ctx.fillStyle = p.c;
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
        ctx.restore();
      });
      if (e < 3400) requestAnimationFrame(frame);
      else canvas.remove();
    };
    requestAnimationFrame(frame);
  } catch (e) {
    /* no-op */
  }
}

function CertificateSVG({ svgRef, name, company, dateStr }) {
  return (
    <svg
      ref={svgRef}
      viewBox="0 0 800 560"
      style={{ width: '100%', maxWidth: 640, display: 'block', borderRadius: 10 }}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="sp-bg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#0e1116" />
          <stop offset="1" stopColor="#191d25" />
        </linearGradient>
        <linearGradient id="sp-gold" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#f7ecb4" />
          <stop offset="0.5" stopColor="#d4af37" />
          <stop offset="1" stopColor="#a9821c" />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="800" height="560" fill="url(#sp-bg)" />
      <rect x="18" y="18" width="764" height="524" fill="none" stroke="url(#sp-gold)" strokeWidth="2" rx="12" />
      <rect x="30" y="30" width="740" height="500" fill="none" stroke={GOLD} strokeWidth="0.6" opacity="0.45" rx="9" />
      <text x="400" y="98" textAnchor="middle" fill={GOLD} fontFamily="Georgia, serif" fontSize="14" letterSpacing="6">
        CERTIFICATE OF MASTERY
      </text>
      <text x="400" y="178" textAnchor="middle" fill="url(#sp-gold)" fontFamily="Georgia, serif" fontSize="60" fontWeight="bold" letterSpacing="2">
        STAFFED PRO
      </text>
      <text x="400" y="228" textAnchor="middle" fill="#8b94a3" fontFamily="Georgia, serif" fontSize="16" fontStyle="italic">
        This certifies that
      </text>
      <text x="400" y="288" textAnchor="middle" fill="#ffffff" fontFamily="Georgia, serif" fontSize="40" fontWeight="bold">
        {name}
      </text>
      <line x1="250" y1="310" x2="550" y2="310" stroke={GOLD} strokeWidth="1" opacity="0.55" />
      <text x="400" y="358" textAnchor="middle" fill="#c7cdd6" fontFamily="Georgia, serif" fontSize="17">
        has completed every guided walkthrough in STAFFED
      </text>
      <text x="400" y="386" textAnchor="middle" fill="#c7cdd6" fontFamily="Georgia, serif" fontSize="17">
        and mastered the full platform.
      </text>
      {company ? (
        <text x="400" y="440" textAnchor="middle" fill={GOLD} fontFamily="Georgia, serif" fontSize="15" letterSpacing="3">
          {company.toUpperCase()}
        </text>
      ) : null}
      <text x="400" y="502" textAnchor="middle" fill="#6b7280" fontFamily="Georgia, serif" fontSize="13">
        Awarded {dateStr}
      </text>
      <circle cx="678" cy="474" r="40" fill="none" stroke="url(#sp-gold)" strokeWidth="2" />
      <circle cx="678" cy="474" r="32" fill="none" stroke={GOLD} strokeWidth="0.6" opacity="0.6" />
      <text x="678" y="470" textAnchor="middle" fill={GOLD} fontFamily="Georgia, serif" fontSize="11" fontWeight="bold">
        STAFFED
      </text>
      <text x="678" y="486" textAnchor="middle" fill={GOLD} fontFamily="Georgia, serif" fontSize="9" letterSpacing="2">
        PRO
      </text>
    </svg>
  );
}

export default function StaffedProCertificate() {
  // Use the effective user from role-preview so admins who flip into
  // staff-preview mode automatically get the staff walkthrough set (and vice
  // versa). Staff role → 'staff' context; anyone admin-like → 'admin' context.
  const { effectiveUser } = useRolePreview();
  const viewerContext = isAdminLikeUser(effectiveUser) ? 'admin' : 'staff';
  const entries = useMemo(() => getTourEntries(viewerContext), [viewerContext]);
  const total = entries.length;
  const user = useMemo(() => readUser(), []);
  // Poll tick — bumped every 2s + on 'storage' events. Combined with `entries`
  // it drives the derived `completedKeys` set below, which auto-resets when the
  // viewer context switches (e.g. admin flips into staff preview).
  const [pollTick, setPollTick] = useState(0);
  const completedKeys = useMemo(() => {
    const s = new Set();
    entries.forEach((e) => {
      if (appStorage.getItem(e.key) === '1') s.add(e.key);
    });
    return s;
  }, [entries, pollTick]);
  const [open, setOpen] = useState(false);
  const [awarded, setAwarded] = useState(() => appStorage.getItem(AWARD_KEY) === '1');
  const [name, setName] = useState(user.name);
  const svgRef = useRef(null);

  const completed = completedKeys.size;
  const isComplete = total > 0 && completed >= total;
  const dateStr = useMemo(
    () => new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }),
    []
  );

  // Poll appStorage for newly-completed tours (same-tab changes don't fire 'storage').
  useEffect(() => {
    if (total === 0) return undefined;
    const bump = () => setPollTick((n) => n + 1);
    const id = setInterval(bump, 2000);
    window.addEventListener('storage', bump);
    return () => {
      clearInterval(id);
      window.removeEventListener('storage', bump);
    };
  }, [total]);

  // One-time award moment when the last walkthrough is completed.
  useEffect(() => {
    // BLOCK S7 2 — the Help panel opens this modal in place of the old chip.
    const onOpen = () => {
      setOpen(true);
      if (isComplete) setTimeout(fireConfetti, 200);
    };
    window.addEventListener('staffed:certificate:open', onOpen);
    return () => window.removeEventListener('staffed:certificate:open', onOpen);
  }, [isComplete]);

  // One-time award moment when the last walkthrough is completed.
  useEffect(() => {
    if (isComplete && !awarded) {
      setAwarded(true);
      try {
        appStorage.setItem(AWARD_KEY, '1');
      } catch (e) {
        /* no-op */
      }
      // Best-effort backend persist so admins can see the STAFFED PRO
      // badge on the leaderboard. Never throws — failure is silent.
      staffAPI.markStaffedPro().catch(() => {});
      setOpen(true);
      const id = setTimeout(fireConfetti, 250);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [isComplete, awarded]);

  const download = useCallback(async () => {
    const svg = svgRef.current;
    if (!svg) return;
    try {
      const xml = new XMLSerializer().serializeToString(svg);
      const svgUrl = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(xml)));
      const img = new Image();
      img.src = svgUrl;
      await (img.decode
        ? img.decode()
        : new Promise((res, rej) => { img.onload = res; img.onerror = rej; }));
      const c = document.createElement('canvas');
      c.width = 1600;
      c.height = 1120;
      const ctx = c.getContext('2d');
      ctx.scale(2, 2);
      ctx.drawImage(img, 0, 0, 800, 560);
      const blob = await new Promise((res) => c.toBlob(res, 'image/png'));
      if (!blob) return;
      const file = new File([blob], 'STAFFED-PRO-Certificate.png', { type: 'image/png' });
      const ua = navigator.userAgent || '';
      const isIOS = /iP(hone|ad|od)/.test(ua) || (/Mac/.test(ua) && navigator.maxTouchPoints > 1);
      const isMobile = isIOS || /Android/.test(ua);
      // Mobile: use the native share sheet so the user can Save to Photos / Files.
      if (isMobile && navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: 'STAFFED PRO Certificate' });
          return;
        } catch (err) {
          if (err && err.name === 'AbortError') return; // user dismissed share sheet
          // otherwise fall through to the fallbacks below
        }
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const canAnchorDownload = 'download' in a && !isIOS; // iOS Safari ignores the download attribute
      if (canAnchorDownload) {
        a.href = url;
        a.download = 'STAFFED-PRO-Certificate.png';
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        // iOS without Web Share: open the PNG so the user can long-press → Save to Photos.
        window.open(url, '_blank');
      }
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) {
      /* no-op */
    }
  }, []);

  if (total === 0) return null;

  const displayName = (name && name.trim()) || 'A STAFFED Power User';
  const remaining = entries.filter((e) => !completedKeys.has(e.key));
  const pct = Math.round((completed / total) * 100);
  const R = 34;
  const C = 2 * Math.PI * R;

  return (
    <>
      {/* BLOCK S7 2 — the header chip is replaced by the "? Help" button
          (HelpMenu). This component stays mounted for the certificate modal +
          award logic; it opens via the `staffed:certificate:open` event. */}

      {open && (
        <OverlayPortal>
        <div
          role="dialog"
          aria-modal="true"
          data-testid="staffed-pro-modal"
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 2147483001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.75)',
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'relative',
              width: '100%',
              maxWidth: 680,
              background: '#12151b',
              border: '1px solid rgba(212,175,55,0.25)',
              borderRadius: 18,
              padding: 20,
              boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
            }}
          >
            <button
              type="button"
              aria-label="Close"
              data-testid="staffed-pro-close"
              onClick={() => setOpen(false)}
              style={{
                position: 'absolute',
                right: 12,
                top: 12,
                zIndex: 5,
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(8,10,16,0.72)',
                border: '1px solid rgba(255,255,255,0.14)',
                borderRadius: 9999,
                color: '#cbd2dc',
                fontSize: 20,
                lineHeight: 1,
                cursor: 'pointer',
              }}
            >
              ×
            </button>

            {isComplete ? (
              <div>
                <CertificateSVG svgRef={svgRef} name={displayName} company={user.company} dateStr={dateStr} />
                <div
                  style={{
                    display: 'flex',
                    gap: 10,
                    alignItems: 'center',
                    marginTop: 16,
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                  }}
                >
                  <input
                    value={name}
                    data-testid="staffed-pro-name-input"
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    style={{
                      flex: 1,
                      minWidth: 180,
                      background: '#0e1116',
                      border: '1px solid #2a2f39',
                      borderRadius: 10,
                      color: '#fff',
                      padding: '10px 12px',
                      fontSize: 14,
                    }}
                  />
                  <button
                    type="button"
                    data-testid="staffed-pro-download"
                    onClick={download}
                    style={{
                      background: 'linear-gradient(135deg,#f7ecb4,#d4af37)',
                      color: '#1a1400',
                      fontWeight: 700,
                      border: 'none',
                      borderRadius: 10,
                      padding: '11px 18px',
                      fontSize: 14,
                      cursor: 'pointer',
                    }}
                  >
                    Download certificate
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '14px 6px 6px' }}>
                <div style={{ position: 'relative', width: 96, height: 96, margin: '0 auto 14px' }}>
                  <svg width="96" height="96" viewBox="0 0 96 96">
                    <circle cx="48" cy="48" r={R} fill="none" stroke="#242a34" strokeWidth="8" />
                    <circle
                      cx="48"
                      cy="48"
                      r={R}
                      fill="none"
                      stroke={GOLD}
                      strokeWidth="8"
                      strokeLinecap="round"
                      strokeDasharray={C}
                      strokeDashoffset={C * (1 - completed / total)}
                      transform="rotate(-90 48 48)"
                    />
                  </svg>
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: 18,
                    }}
                  >
                    {pct}%
                  </div>
                </div>
                <h3 style={{ color: '#fff', fontSize: 20, fontWeight: 700, margin: '0 0 6px' }}>
                  {completed} of {total} walkthroughs complete
                </h3>
                <p style={{ color: '#9aa3b0', fontSize: 14, margin: '0 auto 14px', maxWidth: 420, lineHeight: 1.5 }}>
                  Tap <span style={{ color: GOLD, fontWeight: 600 }}>Show me</span> on any page you haven&apos;t
                  explored yet. Finish them all to unlock your STAFFED PRO certificate.
                </p>
                {remaining.length > 0 && (
                  <div style={{ color: '#c7cdd6', fontSize: 12.5, lineHeight: 1.6, maxWidth: 460, margin: '0 auto' }}>
                    <div style={{ color: '#7b8494', textTransform: 'uppercase', letterSpacing: 1, fontSize: 10, marginBottom: 4 }}>
                      Still to explore
                    </div>
                    {remaining.slice(0, 6).map((e) => e.title).join(' · ')}
                    {remaining.length > 6 ? ` · +${remaining.length - 6} more` : ''}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  style={{
                    marginTop: 18,
                    background: 'transparent',
                    color: GOLD,
                    border: `1px solid ${GOLD}`,
                    borderRadius: 10,
                    padding: '9px 18px',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Keep exploring
                </button>
              </div>
            )}
          </div>
        </div>
        </OverlayPortal>
      )}
    </>
  );
}
